import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileBarChart, Download, Clock } from "lucide-react";

const STATUS_CONFIG: Record<string, { cls: string; label: string }> = {
  pending:    { cls: "bg-amber-100 text-amber-700",   label: "Pending" },
  generating: { cls: "bg-blue-100 text-blue-700",     label: "Generating" },
  complete:   { cls: "bg-emerald-100 text-emerald-700", label: "Complete" },
  failed:     { cls: "bg-red-100 text-red-700",       label: "Failed" },
};

export function MonthlyReports() {
  const { orgId } = useOrgPermissions();

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ["monthly-reports", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("monthly_reports" as any)
        .select("*")
        .eq("org_id", orgId!)
        .order("report_month", { ascending: false }) as any);
      return (data ?? []) as any[];
    },
  });

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <FileBarChart className="w-4 h-4 text-[#C8A84B]" />
            Monthly Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No monthly reports available yet.</p>
              <p className="text-justify text-xs text-muted-foreground mt-1">Reports are generated automatically at the end of each month.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((report: any) => {
                const sc = STATUS_CONFIG[report.status] ?? STATUS_CONFIG.pending;
                const month = new Date(report.report_month).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
                return (
                  <div key={report.id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                    <div className="flex items-center gap-3">
                      <FileBarChart className="w-5 h-5 text-[#C8A84B] shrink-0" />
                      <div>
                        <p className="font-medium text-sm">{month}</p>
                        {report.summary_text && (
                          <p className="text-justify text-xs text-muted-foreground mt-0.5 line-clamp-2">{report.summary_text}</p>
                        )}
                        {report.sent_at && (
                          <p className="text-[11px] text-muted-foreground">
                            Sent {new Date(report.sent_at).toLocaleDateString("en-GB")}
                            {report.sent_to?.length > 0 && ` to ${report.sent_to.length} recipient(s)`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={`text-[10px] ${sc.cls}`}>{sc.label}</Badge>
                      {report.pdf_url && report.status === "complete" && (
                        <a
                          href={report.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
                          title="Download PDF"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
